import { Injectable, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { DragDirection, ExamEventType } from '../models/exam-event.model';
import { ExamEventTrackerService } from '../services/exam-event-tracker.service';
import {
  DEFAULT_MOCK_TEST_SECURITY_CONFIG,
  MOCK_TEST_SECURITY_CONFIG,
  SecurityViolationAction,
  SecurityViolationType,
  VIOLATION_MESSAGES,
  isMockTestProtectedUrl,
} from './mock-test-security.model';

/**
 * Maps every violation type onto its client-sendable WS event
 * (mock-test/mock-test-websocket-events 1.md). `withQuestion` attributes the
 * event to the question on screen, like the content-protection examples in
 * full-event-payloads 4.md.
 */
const SECURITY_VIOLATION_TO_WS_EVENT: Record<
  SecurityViolationType,
  { eventType: ExamEventType; payload?: Record<string, unknown>; withQuestion?: boolean }
> = {
  [SecurityViolationType.COPY_ATTEMPT]: { eventType: 'COPY_ATTEMPTED', withQuestion: true },
  [SecurityViolationType.CUT_ATTEMPT]: { eventType: 'CUT_ATTEMPTED', withQuestion: true },
  [SecurityViolationType.PASTE_ATTEMPT]: { eventType: 'PASTE_ATTEMPTED', withQuestion: true },
  [SecurityViolationType.CONTEXT_MENU]: { eventType: 'RIGHT_CLICK_ATTEMPTED', withQuestion: true },
  [SecurityViolationType.DRAG_ATTEMPT]: {
    eventType: 'DRAG_ATTEMPTED',
    payload: { direction: 'OUT' satisfies DragDirection },
    withQuestion: true,
  },
  [SecurityViolationType.DROP_ATTEMPT]: {
    eventType: 'DRAG_ATTEMPTED',
    payload: { direction: 'IN' satisfies DragDirection },
    withQuestion: true,
  },
  [SecurityViolationType.PRINT_ATTEMPT]: { eventType: 'PRINT_ATTEMPTED' },
  // DEVTOOLS_DETECTED was retired in favor of DEVTOOLS_SUSPECTED — detection
  // is heuristic either way; `heuristic` says which signal fired.
  [SecurityViolationType.DEVTOOLS_SHORTCUT]: {
    eventType: 'DEVTOOLS_SUSPECTED',
    payload: { heuristic: 'keyboard_shortcut' },
  },
  [SecurityViolationType.DEVTOOLS_DETECTED]: {
    eventType: 'DEVTOOLS_SUSPECTED',
    payload: { heuristic: 'window_size_delta' },
  },
  // Only the key press is observed — SCREEN_CAPTURE_DETECTED is reserved
  // for an OS-confirmed capture, which browsers never expose.
  [SecurityViolationType.SCREENSHOT_ATTEMPT]: { eventType: 'SCREENSHOT_SHORTCUT_DETECTED' },
  // Fired when the page's own getDisplayMedia (Screen Capture API) is
  // invoked, i.e. a screen-sharing session was attempted (and blocked).
  [SecurityViolationType.SCREEN_RECORDING_ATTEMPT]: {
    eventType: 'SCREEN_SHARING_DETECTED',
    payload: { source: 'getDisplayMedia', blocked: true },
  },
  // Ctrl/Cmd+A is a text-selection attempt, so it uses the dedicated event.
  [SecurityViolationType.SELECT_ALL_ATTEMPT]: { eventType: 'TEXT_SELECTION_ATTEMPTED', withQuestion: true },
  // No dedicated event for these — reported as the one catch-all
  // browser-restriction event, whose `reason` the backend shape-validates.
  [SecurityViolationType.VIEW_SOURCE_ATTEMPT]: {
    eventType: 'BROWSER_RESTRICTION_TRIGGERED',
    payload: { reason: 'view_source_shortcut_blocked' },
  },
  [SecurityViolationType.SAVE_ATTEMPT]: {
    eventType: 'BROWSER_RESTRICTION_TRIGGERED',
    payload: { reason: 'save_shortcut_blocked' },
  },
  // Leaving the exam window. The raw BROWSER_TAB_HIDDEN / WINDOW_BLURRED
  // signals are still sent separately; these are the counted violations
  // (BROWSER_RESTRICTION_TRIGGERED feeds integrityViolationCount, which drives
  // the server's PROCTORING_WARNING / VIOLATION_LIMIT_EXCEEDED flow).
  [SecurityViolationType.TAB_SWITCH]: {
    eventType: 'BROWSER_RESTRICTION_TRIGGERED',
    payload: { reason: 'tab_switch' },
    withQuestion: true,
  },
  [SecurityViolationType.DISPLAY_SWITCH]: {
    eventType: 'BROWSER_RESTRICTION_TRIGGERED',
    payload: { reason: 'window_moved_to_another_display' },
    withQuestion: true,
  },
  [SecurityViolationType.KEYBOARD_SHORTCUT]: {
    eventType: 'BROWSER_RESTRICTION_TRIGGERED',
    payload: { reason: 'keyboard_shortcut_blocked' },
  },
  [SecurityViolationType.MICROPHONE_ATTEMPT]: {
    eventType: 'MICROPHONE_PERMISSION_CHANGED',
    payload: { blocked: true },
  },
};

/** Screen-level window-placement violations that one physical action can trigger together. */
const WINDOW_PLACEMENT_VIOLATIONS: ReadonlySet<SecurityViolationType> = new Set([
  SecurityViolationType.TAB_SWITCH,
  SecurityViolationType.DISPLAY_SWITCH,
]);

export interface MockTestLockState {
  active: boolean;
  violation: SecurityViolationType | null;
  /** true once repeated violations have escalated to an actual exam termination. */
  terminal?: boolean;
}

const ACTIVE_BODY_CLASS = 'mock-test-active';
const OBSCURED_BODY_CLASS = 'mock-test-obscured';
/** Protected pages that show no exam content, so they are never blurred when the window loses focus. */
const NEVER_OBSCURED_URL_PATTERN = /\/test-series\/[^/]+\/examination-submitted(?:[/?#].*)?$/;
const DEVTOOLS_SIZE_THRESHOLD_PX = 160;
const DEVTOOLS_POLL_INTERVAL_MS = 1000;
const DEVTOOLS_CONFIRM_TICKS = 2;
/** console.table must be this many times slower than console.log to count as devtools being open. */
const DEVTOOLS_CONSOLE_SLOWDOWN_FACTOR = 10;
const DEVTOOLS_CONSOLE_PROBE_ROWS = 50;
const DEVTOOLS_CONSOLE_PROBE_COLUMNS = 500;
/** How long exam content stays blurred after a screenshot shortcut is detected. */
const CAPTURE_SHIELD_DURATION_MS = 1500;
/** TEXT_SELECTION_ATTEMPTED is debounced to one event per burst (event contract §4). */
const TEXT_SELECTION_DEBOUNCE_MS = 1000;
/**
 * Dragging a tab onto another monitor hides the tab and changes the display
 * in one go, so window-placement violations within this window of each other
 * count once.
 */
const WINDOW_PLACEMENT_VIOLATION_COOLDOWN_MS = 3000;
/**
 * How often focus is re-checked. Android Chrome can leave the page without a
 * blur event when the notification shade / quick settings is pulled over it,
 * so the poll catches the focus change the browser never announced.
 */
const FOCUS_POLL_INTERVAL_MS = 250;
/** A touch starting this close to the top edge may be the system swipe that pulls down the notification shade. */
const SYSTEM_EDGE_SWIPE_ZONE_PX = 48;
/** How far an edge touch must travel downward to count as pulling the shade rather than tapping the header. */
const SYSTEM_EDGE_SWIPE_MIN_DISTANCE_PX = 40;
/**
 * The exam area of the question paper (section tabs, question, options). Text
 * selection — by mouse, touch or Ctrl/Cmd+A — is still blocked everywhere,
 * but TEXT_SELECTION_ATTEMPTED is only sent for Ctrl/Cmd+A pressed while
 * focus or the mouse is inside this area, not on the palette, header or popups.
 */
const EXAM_AREA_SELECTOR = '[data-exam-content]';

/**
 * Centralized frontend deterrence layer for the mock-test exam flow: blocks
 * common copy/print/view-source/devtools shortcuts, disables the context
 * menu and text selection, and raises a lock overlay on stronger signals
 * (devtools-shortcut, devtools-detected) that re-locks until devtools is
 * closed — repeated violations never auto-submit or terminate the exam.
 * This is a deterrent against normal-user actions, not a security boundary —
 * the JWT-protected backend remains the actual boundary; a technically
 * skilled user can still inspect network traffic.
 */
@Injectable({ providedIn: 'root' })
export class MockTestSecurityService {
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly eventTracker = inject(ExamEventTrackerService);
  private readonly config = inject(MOCK_TEST_SECURITY_CONFIG, { optional: true }) ?? DEFAULT_MOCK_TEST_SECURITY_CONFIG;

  private readonly activeState = signal(false);
  private readonly lockStateValue = signal<MockTestLockState>({ active: false, violation: null });
  private readonly obscuredState = signal(false);
  private readonly terminationRequestedValue = signal(0);
  // Driven by the exam's enableProctoring setting. When off, restricted
  // actions are still blocked and reported over the WS, but never toasted or locked.
  private readonly proctoringEnabledState = signal(false);

  readonly isActive = this.activeState.asReadonly();
  readonly lockState = this.lockStateValue.asReadonly();
  readonly isObscured = this.obscuredState.asReadonly();
  readonly terminationRequested = this.terminationRequestedValue.asReadonly();
  readonly isProctoringEnabled = this.proctoringEnabledState.asReadonly();

  private readonly examFocusChangesSubject = new Subject<boolean>();
  /**
   * Emits whenever the exam screen loses (false) or regains (true) focus,
   * from every signal combined: window blur/focus, the tab being hidden, a
   * focus change the browser never announced (caught by the poll), and the
   * notification shade / quick settings being pulled over the page. The
   * question paper reports these as WINDOW_BLURRED / WINDOW_FOCUSED.
   */
  readonly examFocusChanges$ = this.examFocusChangesSubject.asObservable();

  private devtoolsPollId: ReturnType<typeof setInterval> | null = null;
  private devtoolsConsecutiveTicks = 0;
  private listenersAttached = false;
  private readonly violationCounts = new Map<SecurityViolationType, number>();
  private captureShieldTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private originalGetDisplayMedia: MediaDevices['getDisplayMedia'] | null = null;
  private blockedGetDisplayMedia: MediaDevices['getDisplayMedia'] | null = null;
  private originalGetUserMedia: MediaDevices['getUserMedia'] | null = null;
  private guardedGetUserMedia: MediaDevices['getUserMedia'] | null = null;
  private lastTextSelectionReportAt = 0;
  /** Element the mouse was last over, so Ctrl/Cmd+A can tell whether it was pressed over the exam area. */
  private lastPointerTarget: EventTarget | null = null;
  /** Integrity checks already reported this activation, so a persistent failure isn't re-sent every tick. */
  private readonly reportedIntegrityFailures = new Set<string>();
  /** Identifies the monitor the exam window is currently on (see getDisplayKey). */
  private currentDisplayKey: string | null = null;
  private lastWindowPlacementViolationAt = 0;
  private focusPollId: ReturnType<typeof setInterval> | null = null;
  /** Page focus the listeners last acted on, so the poll can spot a change no event announced. */
  private lastPageFocus = true;
  /** Exam focus last emitted on examFocusChanges$. */
  private examFocused = true;
  /** true while the notification shade / quick settings is believed to cover the exam. */
  private systemOverlaySuspected = false;
  /** NOTIFICATION_SHADE_OPENED is sent once per opening, however many signals detect it. */
  private notificationShadeReported = false;
  /** Where the current single-finger touch began, while it began in the top edge zone (see SYSTEM_EDGE_SWIPE_ZONE_PX). */
  private edgeTouchStartY: number | null = null;
  /** How far down the current edge touch has travelled. */
  private edgeTouchDistance = 0;

  private readonly boundOnKeyDown = this.onKeyDown.bind(this);
  private readonly boundOnKeyUp = this.onKeyUp.bind(this);
  private readonly boundOnContextMenu = this.onContextMenu.bind(this);
  private readonly boundOnCopy = this.onCopyOrCut.bind(this, SecurityViolationType.COPY_ATTEMPT);
  private readonly boundOnCut = this.onCopyOrCut.bind(this, SecurityViolationType.CUT_ATTEMPT);
  private readonly boundOnPaste = this.onCopyOrCut.bind(this, SecurityViolationType.PASTE_ATTEMPT);
  private readonly boundOnSelectStart = this.onSelectStart.bind(this);
  private readonly boundOnSelectionPointerDown = this.onSelectionPointerDown.bind(this);
  private readonly boundOnSelectionPointerMove = this.onSelectionPointerMove.bind(this);
  private readonly boundOnSelectionChange = this.onSelectionChange.bind(this);
  private readonly boundOnDragStart = this.onDragStart.bind(this);
  private readonly boundOnDragOver = this.onDragOver.bind(this);
  private readonly boundOnDrop = this.onDrop.bind(this);
  private readonly boundOnBeforePrint = this.onBeforePrint.bind(this);
  private readonly boundOnVisibilityChange = this.onFocusSignal.bind(this);
  private readonly boundOnTouchStart = this.onTouchStart.bind(this);
  private readonly boundOnTouchMove = this.onTouchMove.bind(this);
  private readonly boundOnTouchEnd = this.onTouchEnd.bind(this);
  private readonly boundOnTouchCancel = this.onTouchCancel.bind(this);
  private readonly boundOnScreenChange = this.checkDisplaySwitch.bind(this);

  constructor() {
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd) || !this.activeState()) {
        return;
      }

      if (!isMockTestProtectedUrl(event.urlAfterRedirects)) {
        this.deactivate();
        return;
      }

      this.updateObscuredState();
    });
  }

  activate(): void {
    if (this.listenersAttached) {
      return;
    }

    this.listenersAttached = true;
    this.activeState.set(true);
    document.body.classList.add(ACTIVE_BODY_CLASS);

    document.addEventListener('keydown', this.boundOnKeyDown, true);
    document.addEventListener('keyup', this.boundOnKeyUp, true);
    document.addEventListener('contextmenu', this.boundOnContextMenu, true);
    document.addEventListener('copy', this.boundOnCopy, true);
    document.addEventListener('cut', this.boundOnCut, true);
    document.addEventListener('paste', this.boundOnPaste, true);
    document.addEventListener('selectstart', this.boundOnSelectStart, true);
    document.addEventListener('pointerdown', this.boundOnSelectionPointerDown, true);
    document.addEventListener('pointermove', this.boundOnSelectionPointerMove, true);
    document.addEventListener('selectionchange', this.boundOnSelectionChange);
    document.addEventListener('dragstart', this.boundOnDragStart, true);
    document.addEventListener('dragover', this.boundOnDragOver, true);
    document.addEventListener('drop', this.boundOnDrop, true);
    window.addEventListener('beforeprint', this.boundOnBeforePrint);
    document.addEventListener('visibilitychange', this.boundOnVisibilityChange);
    window.addEventListener('blur', this.boundOnVisibilityChange);
    window.addEventListener('focus', this.boundOnVisibilityChange);
    document.addEventListener('touchstart', this.boundOnTouchStart, { capture: true, passive: true });
    document.addEventListener('touchmove', this.boundOnTouchMove, { capture: true, passive: true });
    document.addEventListener('touchend', this.boundOnTouchEnd, { capture: true, passive: true });
    document.addEventListener('touchcancel', this.boundOnTouchCancel, { capture: true, passive: true });
    (screen as Screen & Partial<EventTarget>).addEventListener?.('change', this.boundOnScreenChange);
    this.blockScreenCaptureApi();
    this.blockMicrophoneApi();

    this.currentDisplayKey = this.getDisplayKey();
    this.lastWindowPlacementViolationAt = 0;

    this.devtoolsConsecutiveTicks = 0;
    this.lastTextSelectionReportAt = 0;
    this.reportedIntegrityFailures.clear();
    this.devtoolsPollId = setInterval(() => this.pollForDevtools(), DEVTOOLS_POLL_INTERVAL_MS);

    this.clearSystemOverlay();
    this.lastPageFocus = this.hasPageFocus();
    this.examFocused = this.lastPageFocus;
    this.focusPollId = setInterval(() => this.pollFocus(), FOCUS_POLL_INTERVAL_MS);
  }

  deactivate(): void {
    if (!this.listenersAttached) {
      return;
    }

    this.listenersAttached = false;
    this.activeState.set(false);
    this.lockStateValue.set({ active: false, violation: null });
    this.obscuredState.set(false);
    this.violationCounts.clear();
    document.body.classList.remove(ACTIVE_BODY_CLASS, OBSCURED_BODY_CLASS);

    document.removeEventListener('keydown', this.boundOnKeyDown, true);
    document.removeEventListener('keyup', this.boundOnKeyUp, true);
    document.removeEventListener('contextmenu', this.boundOnContextMenu, true);
    document.removeEventListener('copy', this.boundOnCopy, true);
    document.removeEventListener('cut', this.boundOnCut, true);
    document.removeEventListener('paste', this.boundOnPaste, true);
    document.removeEventListener('selectstart', this.boundOnSelectStart, true);
    document.removeEventListener('pointerdown', this.boundOnSelectionPointerDown, true);
    document.removeEventListener('pointermove', this.boundOnSelectionPointerMove, true);
    document.removeEventListener('selectionchange', this.boundOnSelectionChange);
    this.lastPointerTarget = null;
    document.removeEventListener('dragstart', this.boundOnDragStart, true);
    document.removeEventListener('dragover', this.boundOnDragOver, true);
    document.removeEventListener('drop', this.boundOnDrop, true);
    window.removeEventListener('beforeprint', this.boundOnBeforePrint);
    document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
    window.removeEventListener('blur', this.boundOnVisibilityChange);
    window.removeEventListener('focus', this.boundOnVisibilityChange);
    document.removeEventListener('touchstart', this.boundOnTouchStart, true);
    document.removeEventListener('touchmove', this.boundOnTouchMove, true);
    document.removeEventListener('touchend', this.boundOnTouchEnd, true);
    document.removeEventListener('touchcancel', this.boundOnTouchCancel, true);
    (screen as Screen & Partial<EventTarget>).removeEventListener?.('change', this.boundOnScreenChange);
    this.restoreScreenCaptureApi();
    this.restoreMicrophoneApi();
    this.currentDisplayKey = null;

    if (this.captureShieldTimeoutId) {
      clearTimeout(this.captureShieldTimeoutId);
      this.captureShieldTimeoutId = null;
    }

    if (this.devtoolsPollId) {
      clearInterval(this.devtoolsPollId);
      this.devtoolsPollId = null;
    }

    if (this.focusPollId) {
      clearInterval(this.focusPollId);
      this.focusPollId = null;
    }

    this.clearSystemOverlay();
    this.edgeTouchStartY = null;
  }

  setProctoringEnabled(enabled: boolean): void {
    this.proctoringEnabledState.set(enabled);

    if (!enabled) {
      this.devtoolsConsecutiveTicks = 0;
      this.resolveLock();
    }
  }

  resolveLock(): void {
    this.lockStateValue.set({ active: false, violation: null });
  }

  /**
   * Same window-size heuristic as the background poll, exposed so callers
   * (start/resume popup, lock overlay) can refuse to proceed while devtools
   * are docked open. Undocked devtools windows can't be detected this way.
   */
  isDevtoolsOpen(): boolean {
    const widthDelta = window.outerWidth - window.innerWidth;
    const heightDelta = window.outerHeight - window.innerHeight;
    return widthDelta > DEVTOOLS_SIZE_THRESHOLD_PX || heightDelta > DEVTOOLS_SIZE_THRESHOLD_PX;
  }

  /**
   * Catches devtools in any mode the size check misses: undocked windows and
   * device emulation (which fakes outerWidth/outerHeight to match the emulated
   * viewport). Console calls are near-free while devtools are closed, but an
   * open console has to render them, and console.table of a large object is
   * far slower to render than console.log of the same object. Chromium only;
   * returns false elsewhere. Clears the console, so only run it on explicit
   * start/resume actions, not in the background poll.
   */
  isDevtoolsConsoleAttached(): boolean {
    const probe = Array.from({ length: DEVTOOLS_CONSOLE_PROBE_ROWS }, () => {
      const row: Record<string, string> = {};
      for (let i = 0; i < DEVTOOLS_CONSOLE_PROBE_COLUMNS; i++) {
        row[`${i}`] = `${i}`;
      }
      return row;
    });

    const tableStartedAt = performance.now();
    console.table(probe);
    const tableTime = performance.now() - tableStartedAt;

    const logStartedAt = performance.now();
    console.log(probe);
    const logTime = performance.now() - logStartedAt;

    console.clear();

    return tableTime > 0 && logTime > 0 && tableTime > logTime * DEVTOOLS_CONSOLE_SLOWDOWN_FACTOR;
  }

  reportViolation(type: SecurityViolationType, extraPayload: Record<string, unknown> = {}): void {
    if (WINDOW_PLACEMENT_VIOLATIONS.has(type)) {
      const now = Date.now();

      if (now - this.lastWindowPlacementViolationAt < WINDOW_PLACEMENT_VIOLATION_COOLDOWN_MS) {
        return;
      }

      this.lastWindowPlacementViolationAt = now;
    }

    const occurrences = (this.violationCounts.get(type) ?? 0) + 1;
    this.violationCounts.set(type, occurrences);

    // Every blocked attempt is reported so the backend knows what the student
    // tried, regardless of proctoring — proctoring only gates the toast/lock.
    const wsEvent = SECURITY_VIOLATION_TO_WS_EVENT[type];
    this.eventTracker.track(
      wsEvent.eventType,
      { ...wsEvent.payload, ...extraPayload, occurrence: occurrences },
      wsEvent.withQuestion ? { questionId: this.eventTracker.activeQuestionId } : {},
    );

    if (!this.proctoringEnabledState()) {
      return;
    }

    // No violation ever escalates to auto-terminating the exam — devtools
    // detection just keeps re-locking the screen until it's closed.
    const action = this.config[type];

    switch (action) {
      case SecurityViolationAction.WARNING:
        this.toastr.warning(VIOLATION_MESSAGES[type], 'Security notice');
        break;
      case SecurityViolationAction.LOCK_SCREEN:
        this.lockStateValue.set({ active: true, violation: type, terminal: false });
        break;
      case SecurityViolationAction.REQUIRE_FULLSCREEN:
        this.requireFullscreen(type);
        break;
      case SecurityViolationAction.TERMINATE_ATTEMPT:
        this.lockStateValue.set({ active: true, violation: type, terminal: true });
        this.terminationRequestedValue.update((count) => count + 1);
        break;
    }
  }

  private requireFullscreen(type: SecurityViolationType): void {
    if (document.fullscreenElement || !document.documentElement.requestFullscreen) {
      return;
    }

    document.documentElement.requestFullscreen().catch(() => {
      this.lockStateValue.set({ active: true, violation: type });
    });
  }

  private onKeyDown(event: KeyboardEvent): void {
    const isModifierCombo = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();

    if (this.isScreenshotShortcut(event)) {
      event.preventDefault();
      this.onScreenshotAttempt();
      return;
    }

    const shortcut = { trigger: 'keyboard_shortcut', shortcut: this.describeShortcut(event) };
    // Holding a key auto-repeats keydown — still blocked, but reported once so
    // the socket isn't flooded (the backend rate-limits per attempt).
    const report = (type: SecurityViolationType) => {
      if (!event.repeat) {
        this.reportViolation(type, shortcut);
      }
    };

    // The browser's own reload button can't be disabled by a page (it gets the
    // beforeunload prompt instead), but the reload keys can, so they're
    // cancelled with the same notice as the disabled back/forward buttons.
    if (this.isRefreshShortcut(event)) {
      event.preventDefault();
      if (!event.repeat) {
        this.toastr.warning('Page refresh is disabled during the examination.', 'Refresh disabled');
        this.reportViolation(SecurityViolationType.KEYBOARD_SHORTCUT, shortcut);
      }
      return;
    }

    // Devtools restriction temporarily disabled.
    // if (key === 'f12') {
    //   event.preventDefault();
    //   report(SecurityViolationType.DEVTOOLS_SHORTCUT);
    //   return;
    // }

    // // Ctrl/Cmd+Shift+I/J/C/K (K = Firefox console) and macOS Cmd+Option+I/J/C/U.
    // const isDevtoolsCombo =
    //   isModifierCombo && (event.shiftKey || event.altKey) && ['i', 'j', 'c', 'k'].includes(key);
    // const isDevtoolsCode = event.metaKey && event.altKey && ['KeyI', 'KeyJ', 'KeyC', 'KeyU'].includes(event.code);

    // if (isDevtoolsCombo || isDevtoolsCode) {
    //   event.preventDefault();
    //   report(SecurityViolationType.DEVTOOLS_SHORTCUT);
    //   return;
    // }

    if (isModifierCombo && !event.shiftKey) {
      if (key === 'u') {
        event.preventDefault();
        report(SecurityViolationType.VIEW_SOURCE_ATTEMPT);
      } else if (key === 's') {
        event.preventDefault();
        report(SecurityViolationType.SAVE_ATTEMPT);
      } else if (key === 'p') {
        event.preventDefault();
        report(SecurityViolationType.PRINT_ATTEMPT);
      } else if (key === 'c') {
        event.preventDefault();
        report(SecurityViolationType.COPY_ATTEMPT);
      } else if (key === 'x') {
        event.preventDefault();
        report(SecurityViolationType.CUT_ATTEMPT);
      } else if (key === 'v') {
        event.preventDefault();
        report(SecurityViolationType.PASTE_ATTEMPT);
      } else if (key === 'a') {
        event.preventDefault();
        // The only source of TEXT_SELECTION_ATTEMPTED: reported when focus or the
        // mouse is on the exam area, debounced so one burst sends one event.
        const overExamArea = this.isInExamArea(event.target) || this.isInExamArea(this.lastPointerTarget);
        if (!event.repeat && overExamArea && this.claimTextSelectionReport()) {
          report(SecurityViolationType.SELECT_ALL_ATTEMPT);
        }
      } else if (this.isBlockedShortcut(event)) {
        event.preventDefault();
        report(SecurityViolationType.KEYBOARD_SHORTCUT);
      }
      return;
    }

    if (this.isBlockedShortcut(event)) {
      event.preventDefault();
      report(SecurityViolationType.KEYBOARD_SHORTCUT);
    }
  }

  /**
   * Everything that isn't plain typing/navigation: Ctrl/Cmd combos, function
   * keys, Alt+Arrow (history) and the Windows/Meta key. Ctrl+Alt is left
   * alone because AltGr produces it when typing symbols on many layouts.
   * Browser-reserved ones (Ctrl+W/T/N, Alt+F4) can't be cancelled by a page.
   */
  private isBlockedShortcut(event: KeyboardEvent): boolean {
    if (event.getModifierState?.('AltGraph')) {
      return false;
    }

    if (/^F([1-9]|1[0-2])$/.test(event.key)) {
      return true;
    }

    if (event.key === 'Meta' || event.key === 'ContextMenu') {
      return true;
    }

    if (event.altKey && !event.ctrlKey && ['arrowleft', 'arrowright', 'home'].includes(event.key.toLowerCase())) {
      return true;
    }

    // Ctrl/Cmd + anything (Ctrl+Alt excluded above via AltGr check is not reliable on every OS).
    return (event.ctrlKey || event.metaKey) && !event.altKey && !['control', 'meta', 'shift', 'alt'].includes(event.key.toLowerCase());
  }

  /** F5, Ctrl/Cmd+R and their hard-reload variants, plus the dedicated BrowserRefresh key. */
  private isRefreshShortcut(event: KeyboardEvent): boolean {
    if (event.key === 'F5' || event.key === 'BrowserRefresh') {
      return true;
    }

    return (event.ctrlKey || event.metaKey) && !event.altKey && event.code === 'KeyR';
  }

  /** e.g. "Ctrl+Shift+I", "Meta+Alt+J", "F12" — sent with the WS event for context. */
  private describeShortcut(event: KeyboardEvent): string {
    const parts: string[] = [];
    if (event.ctrlKey) parts.push('Ctrl');
    if (event.metaKey) parts.push('Meta');
    if (event.altKey) parts.push('Alt');
    if (event.shiftKey) parts.push('Shift');
    parts.push(event.key.length === 1 ? event.key.toUpperCase() : event.key);
    return parts.join('+');
  }

  // Most browsers only deliver PrintScreen on keyup (the OS consumes keydown),
  // so it's handled here as well as in onKeyDown.
  private onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'PrintScreen') {
      event.preventDefault();
      this.onScreenshotAttempt();
    }
  }

  /**
   * PrintScreen, Windows snipping (Win+Shift+S) and macOS capture shortcuts
   * (Cmd+Shift+3/4/5, Ctrl+Cmd+Shift+3/4). The OS often intercepts these
   * before the browser sees them, so this only catches what gets through.
   */
  private isScreenshotShortcut(event: KeyboardEvent): boolean {
    if (event.key === 'PrintScreen') {
      return true;
    }

    if (!event.metaKey || !event.shiftKey) {
      return false;
    }

    return event.code === 'KeyS' || ['Digit3', 'Digit4', 'Digit5'].includes(event.code);
  }

  /**
   * Browsers can't stop an OS-level screenshot, so this is damage limitation:
   * blur the exam content immediately (and for a short while after), and
   * overwrite the clipboard so a PrintScreen capture doesn't survive there.
   */
  private onScreenshotAttempt(): void {
    this.raiseCaptureShield();
    navigator.clipboard?.writeText('').catch(() => undefined);
    this.reportViolation(SecurityViolationType.SCREENSHOT_ATTEMPT);
  }

  private raiseCaptureShield(): void {
    if (this.captureShieldTimeoutId) {
      clearTimeout(this.captureShieldTimeoutId);
    }

    this.captureShieldTimeoutId = setTimeout(() => {
      this.captureShieldTimeoutId = null;
      this.updateObscuredState();
    }, CAPTURE_SHIELD_DURATION_MS);

    this.updateObscuredState();
  }

  /**
   * Disables the in-page Screen Capture API (getDisplayMedia) while the exam
   * is active, so nothing running in this page can start a tab/screen
   * recording. External recorders (OS tools, OBS, phone cameras) are outside
   * the browser's control and can't be blocked from here.
   */
  private blockScreenCaptureApi(): void {
    const mediaDevices = navigator.mediaDevices;

    if (!mediaDevices?.getDisplayMedia || this.originalGetDisplayMedia) {
      return;
    }

    this.originalGetDisplayMedia = mediaDevices.getDisplayMedia;
    this.blockedGetDisplayMedia = () => {
      this.reportViolation(SecurityViolationType.SCREEN_RECORDING_ATTEMPT);
      return Promise.reject(new DOMException('Screen capture is disabled during the test.', 'NotAllowedError'));
    };
    mediaDevices.getDisplayMedia = this.blockedGetDisplayMedia;
  }

  private restoreScreenCaptureApi(): void {
    if (this.originalGetDisplayMedia && navigator.mediaDevices) {
      navigator.mediaDevices.getDisplayMedia = this.originalGetDisplayMedia;
    }

    this.originalGetDisplayMedia = null;
    this.blockedGetDisplayMedia = null;
  }

  /**
   * Rejects in-page microphone requests (getUserMedia with audio) while the
   * exam is active. Video-only requests pass through. This can't stop other
   * apps from using the mic, only this page.
   */
  private blockMicrophoneApi(): void {
    const mediaDevices = navigator.mediaDevices;

    if (!mediaDevices?.getUserMedia || this.originalGetUserMedia) {
      return;
    }

    const original = mediaDevices.getUserMedia;
    this.originalGetUserMedia = original;
    this.guardedGetUserMedia = (constraints?: MediaStreamConstraints) => {
      if (constraints?.audio) {
        this.reportViolation(SecurityViolationType.MICROPHONE_ATTEMPT, { source: 'getUserMedia' });
        return Promise.reject(new DOMException('Microphone access is disabled during the test.', 'NotAllowedError'));
      }

      return original.call(mediaDevices, constraints);
    };
    mediaDevices.getUserMedia = this.guardedGetUserMedia;
  }

  private restoreMicrophoneApi(): void {
    if (this.originalGetUserMedia && navigator.mediaDevices) {
      navigator.mediaDevices.getUserMedia = this.originalGetUserMedia;
    }

    this.originalGetUserMedia = null;
    this.guardedGetUserMedia = null;
  }

  private onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    const fromTouch = (event as Partial<PointerEvent>).pointerType === 'touch';
    this.reportViolation(SecurityViolationType.CONTEXT_MENU, { trigger: fromTouch ? 'long_press' : 'mouse' });
  }

  private onCopyOrCut(type: SecurityViolationType, event: Event): void {
    event.preventDefault();

    // A keyboard shortcut was already blocked and reported in onKeyDown, so
    // this only fires for the context-menu / Edit-menu path.
    this.reportViolation(type, { trigger: 'clipboard_event' });
  }

  // Mouse/touch text selection is silently blocked and not reported —
  // TEXT_SELECTION_ATTEMPTED is only sent for Ctrl/Cmd+A (see onKeyDown).
  // Only reached where selection is still possible (Safari, form fields);
  // body.mock-test-active's user-select: none stops Chromium and Firefox
  // from ever firing selectstart.
  private onSelectStart(event: Event): void {
    event.preventDefault();
  }

  // Remembers the element under the mouse so Ctrl/Cmd+A can tell whether it
  // was pressed over the exam area.
  private onSelectionPointerDown(event: PointerEvent): void {
    this.lastPointerTarget = event.target;
  }

  private onSelectionPointerMove(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.lastPointerTarget = event.target;
    }
  }

  // Event targets can be Text nodes, so those resolve to their parent.
  private toElement(target: EventTarget | Node | null): Element | null {
    return target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  }

  private isInExamArea(target: EventTarget | Node | null): boolean {
    return this.toElement(target)?.closest(EXAM_AREA_SELECTOR) != null;
  }

  // Backstop for selections user-select: none misses (Shift+Arrow, touch
  // handles, an extension overriding user-select): any real selection of
  // test content is cleared (not reported). Form fields are left alone.
  private onSelectionChange(): void {
    const selection = document.getSelection();

    if (!selection || selection.isCollapsed || selection.toString().trim() === '') {
      return;
    }

    const anchor = selection.anchorNode;
    const anchorElement = anchor instanceof Element ? anchor : anchor?.parentElement ?? null;

    if (anchorElement?.closest('input, textarea, [contenteditable=""], [contenteditable="true"]')) {
      return;
    }

    selection.removeAllRanges();
  }

  // Ctrl+A comes in bursts, so report at most once per burst.
  private claimTextSelectionReport(): boolean {
    const now = Date.now();

    if (now - this.lastTextSelectionReportAt < TEXT_SELECTION_DEBOUNCE_MS) {
      return false;
    }

    this.lastTextSelectionReportAt = now;
    return true;
  }

  private onDragStart(event: Event): void {
    event.preventDefault();
    this.reportViolation(SecurityViolationType.DRAG_ATTEMPT);
  }

  // Cancelling dragover is what stops the browser from opening a dropped
  // file in place of the exam; it fires continuously, so it's not reported.
  private onDragOver(event: DragEvent): void {
    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'none';
    }
  }

  private onDrop(event: DragEvent): void {
    event.preventDefault();
    this.reportViolation(SecurityViolationType.DROP_ATTEMPT);
  }

  private onBeforePrint(): void {
    this.reportViolation(SecurityViolationType.PRINT_ATTEMPT);
  }

  private onFocusSignal(): void {
    this.syncFocusState();
  }

  private hasPageFocus(): boolean {
    return document.visibilityState === 'visible' && document.hasFocus();
  }

  /** false while anything — another window, another tab, the notification shade — has the student's attention instead of the exam. */
  hasExamFocus(): boolean {
    return this.hasPageFocus() && !this.systemOverlaySuspected;
  }

  /**
   * Single place every focus signal ends up: updates the blur shield and
   * emits on examFocusChanges$ when the exam's focus actually changed, so one
   * physical action (e.g. a blur followed by visibilitychange) is reported once.
   */
  private syncFocusState(): void {
    const pageFocus = this.hasPageFocus();

    if (pageFocus !== this.lastPageFocus) {
      this.lastPageFocus = pageFocus;

      if (pageFocus) {
        // Focus came back, so whatever took it — the shade included — has closed.
        this.clearSystemOverlay();
      } else {
        this.reportNotificationShade();
      }
    }

    this.updateObscuredState();

    const examFocused = pageFocus && !this.systemOverlaySuspected;

    if (examFocused !== this.examFocused) {
      this.examFocused = examFocused;
      this.examFocusChangesSubject.next(examFocused);
    }
  }

  /**
   * Android Chrome doesn't always fire blur/focus when the notification shade
   * or quick settings is pulled over the page, but document.hasFocus() still
   * flips, so the poll catches the change no event announced.
   */
  private pollFocus(): void {
    this.syncFocusState();
  }

  private onTouchStart(event: TouchEvent): void {
    // Nothing covering the page can still be open once the page itself
    // receives a touch, so the shade has been closed.
    if (this.systemOverlaySuspected) {
      this.clearSystemOverlay();
      this.syncFocusState();
    }

    const touch = event.touches.length === 1 ? event.touches[0] : null;
    this.edgeTouchStartY = touch && touch.clientY <= SYSTEM_EDGE_SWIPE_ZONE_PX ? touch.clientY : null;
    this.edgeTouchDistance = 0;
  }

  private onTouchMove(event: TouchEvent): void {
    if (this.edgeTouchStartY === null || event.touches.length !== 1) {
      this.edgeTouchStartY = null;
      return;
    }

    this.edgeTouchDistance = Math.max(this.edgeTouchDistance, event.touches[0].clientY - this.edgeTouchStartY);
  }

  /**
   * In fullscreen the status bar is hidden, so the swipe that pulls down the
   * notification shade / quick settings starts on the page. Android usually
   * hands that swipe to the page as well and just ends it normally, without
   * any blur or focus change, so a downward swipe from the top edge is taken
   * as the shade being opened.
   */
  private onTouchEnd(): void {
    const pulledDown = this.edgeTouchStartY !== null && this.edgeTouchDistance >= SYSTEM_EDGE_SWIPE_MIN_DISTANCE_PX;
    this.edgeTouchStartY = null;

    if (pulledDown) {
      this.suspectSystemOverlay();
    }
  }

  /** Android may instead cancel the page's touch when the system takes the swipe over. */
  private onTouchCancel(): void {
    const fromEdge = this.edgeTouchStartY !== null;
    this.edgeTouchStartY = null;

    if (fromEdge) {
      this.suspectSystemOverlay();
    }
  }

  /** Blurs the exam and reports it as having lost focus until the page is touched or focused again. */
  private suspectSystemOverlay(): void {
    this.systemOverlaySuspected = true;
    this.reportNotificationShade();
    this.syncFocusState();
  }

  /**
   * Losing focus while the page stays visible on a touch device is what the
   * notification shade / quick settings looks like from inside the browser
   * (the backend contract's "where detectable"). Desktop focus loss is left
   * to WINDOW_BLURRED alone.
   */
  private reportNotificationShade(): void {
    if (
      this.notificationShadeReported ||
      document.visibilityState === 'hidden' ||
      NEVER_OBSCURED_URL_PATTERN.test(this.router.url) ||
      !window.matchMedia('(pointer: coarse)').matches
    ) {
      return;
    }

    this.notificationShadeReported = true;
    this.eventTracker.track('NOTIFICATION_SHADE_OPENED', {}, { questionId: this.eventTracker.activeQuestionId });
  }

  private clearSystemOverlay(): void {
    this.systemOverlaySuspected = false;
    this.notificationShadeReported = false;
  }

  /**
   * Visual-only deterrent: blurs exam content while the tab/window is
   * unfocused or hidden, or the notification shade is suspected over it. Does not report a security violation — tab/window
   * blur is triggered by too many legitimate things (fullscreen permission
   * prompts, OS focus blips, alt-tab habits) to safely auto-submit on.
   */
  private updateObscuredState(): void {
    const shouldObscure =
      !NEVER_OBSCURED_URL_PATTERN.test(this.router.url) &&
      (!this.hasPageFocus() || this.systemOverlaySuspected || this.captureShieldTimeoutId !== null);
    this.obscuredState.set(shouldObscure);
    document.body.classList.toggle(OBSCURED_BODY_CLASS, shouldObscure);
  }

  /**
   * Heuristic only: compares outer/inner window dimensions, which widen when
   * devtools are docked. False positives are possible (e.g. on-screen
   * keyboards, browser zoom), so two consecutive ticks are required before
   * treating it as a real signal.
   */
  private pollForDevtools(): void {
    this.checkPageIntegrity();
    this.checkDisplaySwitch();

    // Devtools restriction temporarily disabled.
    // const suspected = this.isDevtoolsOpen();

    // if (!suspected) {
    //   this.devtoolsConsecutiveTicks = 0;

    //   const currentLock = this.lockStateValue();
    //   if (currentLock.violation === SecurityViolationType.DEVTOOLS_DETECTED && !currentLock.terminal) {
    //     this.resolveLock();
    //   }

    //   return;
    // }

    // this.devtoolsConsecutiveTicks += 1;

    // // Reported (and locked) once per open session. If the candidate resumes
    // // while devtools are still open, the lock is not re-applied; closing and
    // // reopening devtools resets the tick count and triggers it again.
    // if (this.devtoolsConsecutiveTicks === DEVTOOLS_CONFIRM_TICKS) {
    //   this.reportViolation(SecurityViolationType.DEVTOOLS_DETECTED);
    // }
  }

  /**
   * The browser doesn't fire an event when a window is dragged onto another
   * monitor (screen 'change' is Chromium-only and only fires when the new
   * screen's attributes differ), so the current screen's position in the
   * virtual desktop is also compared on every poll tick. Only the origin is
   * compared, so a resolution change on the same monitor (reported as
   * DISPLAY_CONFIGURATION_CHANGED) isn't mistaken for a switch.
   */
  private checkDisplaySwitch(): void {
    const displayKey = this.getDisplayKey();

    if (displayKey === null || this.currentDisplayKey === null) {
      this.currentDisplayKey = displayKey;
      return;
    }

    if (displayKey === this.currentDisplayKey) {
      return;
    }

    const previousDisplay = this.currentDisplayKey;
    this.currentDisplayKey = displayKey;
    this.reportViolation(SecurityViolationType.DISPLAY_SWITCH, {
      previousDisplay,
      currentDisplay: displayKey,
    });
  }

  /**
   * screen.availLeft/availTop (Chromium, Firefox, Safari) are the current
   * monitor's origin in the virtual desktop, so they change exactly when the
   * window lands on another monitor. Returns null where they aren't exposed.
   */
  private getDisplayKey(): string | null {
    const { availLeft, availTop } = screen as Screen & { availLeft?: number; availTop?: number };

    if (typeof availLeft !== 'number' || typeof availTop !== 'number') {
      return null;
    }

    return `${availLeft},${availTop}`;
  }

  /**
   * Verifies the protections this service installed are still in place — a
   * script or extension stripping them (e.g. restoring getDisplayMedia, or
   * removing the body class that disables selection) is reported once per
   * activation and the protection is re-applied.
   */
  private checkPageIntegrity(): void {
    if (!document.body.classList.contains(ACTIVE_BODY_CLASS)) {
      this.reportIntegrityFailure('security_body_class_removed');
      document.body.classList.add(ACTIVE_BODY_CLASS);
    }

    const mediaDevices = navigator.mediaDevices;

    if (this.guardedGetUserMedia && mediaDevices && mediaDevices.getUserMedia !== this.guardedGetUserMedia) {
      this.reportIntegrityFailure('microphone_guard_removed');
      mediaDevices.getUserMedia = this.guardedGetUserMedia;
    }

    if (this.blockedGetDisplayMedia && mediaDevices && mediaDevices.getDisplayMedia !== this.blockedGetDisplayMedia) {
      this.reportIntegrityFailure('screen_capture_guard_removed');
      mediaDevices.getDisplayMedia = this.blockedGetDisplayMedia;
    }
  }

  private reportIntegrityFailure(check: string): void {
    if (this.reportedIntegrityFailures.has(check)) {
      return;
    }

    this.reportedIntegrityFailures.add(check);
    this.eventTracker.track('PAGE_INTEGRITY_CHECK_FAILED', { check });
  }
}
